const LANG_LOCALE_MAP = { en: 'en-PH' };
export const DEFAULT_BUSINESS_TIME_ZONE = 'Asia/Manila';

export const getLanguageCode = () => 'en';

export const getDateLocale = (language = '') => LANG_LOCALE_MAP[getLanguageCode(language)] || 'en-PH';

export const formatLocalizedDateTime = (value, language = '', options = {}) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const { timeZone = DEFAULT_BUSINESS_TIME_ZONE, ...dateTimeOptions } = options;
  return date.toLocaleString(getDateLocale(language), {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...dateTimeOptions
  });
};

export const formatMonthLabel = (month, language = '') => {
  const numericMonth = Number(month);
  if (!Number.isFinite(numericMonth) || numericMonth < 1 || numericMonth > 12) {
    return String(month || '');
  }

  return new Date(2000, numericMonth - 1, 1).toLocaleString(getDateLocale(language), {
    month: 'long'
  });
};

export const formatUnitCount = (t, count) => t('format.unitsCount', {
  count: Number(count || 0),
  defaultValue: `${Number(count || 0)} units`
});

export const normalizeAutogeneratedDeviceName = (name, fallback = '') => {
  const raw = String(name || '').trim();
  const fallbackValue = String(fallback || '').trim();
  if (!raw) return fallbackValue;

  const autogeneratedMatch = raw.match(/^\u8bbe\u5907\s+(\d+)$/u);
  if (autogeneratedMatch) {
    return `Unit ${autogeneratedMatch[1]}`;
  }

  return raw.replace(/^\u8bbe\u5907\s+\u8bbe\u5907\s+/u, 'Unit ');
};

const containsChineseText = (value) => /[\u4e00-\u9fff]/u.test(String(value || ''));

export const getFilterDisplayName = (filter, language = '') => {
  return filter?.displayNameEn
    || filter?.filterKey
    || '-';
};

export const getWaterTypeLabel = (t, waterType) => {
  const normalized = String(waterType || '').toLowerCase();
  if (normalized === 'pure' || normalized === '\u7eaf\u51c0\u6c34') {
    return t('waterTypes.pure', { defaultValue: 'Pure water' });
  }
  if (normalized === 'mineral' || normalized === '\u77ff\u7269\u8d28\u6c34') {
    return t('waterTypes.mineral', { defaultValue: 'Mineral water' });
  }
  return containsChineseText(waterType)
    ? t('waterTypes.unknown', { defaultValue: 'Unknown water type' })
    : waterType || '-';
};

export const getTransactionDisplayType = (t, item = {}) => {
  const key = item.displayTypeKey || item.flowType || item.type;
  const rawDisplay = String(item.displayType || '');

  if (item.isStandalonePhysical || key === 'standalonePhysical' || rawDisplay.includes('\u5b9e\u4f53\u5361\u72ec\u7acb')) {
    return t('transactions.displayType.standalonePhysical', { defaultValue: 'Standalone physical card dispense' });
  }
  if (item.isKudInitialCard || key === 'kudInitialCard' || rawDisplay.includes('\u9996\u6279KUD')) {
    return t('transactions.displayType.kudInitialCard', { defaultValue: 'Initial KUD physical card dispense' });
  }
  if (String(item.payment_provider || '').toLowerCase() === 'payermax' || key === 'payermaxTopup' || rawDisplay.includes('PayerMax')) {
    return t('transactions.displayType.payermaxTopup', { defaultValue: 'PayerMax top up' });
  }
  if (item.balanceSource === 'SIGNUP_BONUS' || key === 'signupBonus') {
    return t('transactions.displayType.signupBonus', { defaultValue: 'Signup bonus' });
  }
  if (key === 'topup' || key === 'appTopup' || rawDisplay.includes('App\u5145\u503c')) {
    return t('transactions.displayType.appTopup', { defaultValue: 'App top up' });
  }
  if (key === 'dispense' || key === 'userDispense' || rawDisplay.includes('\u7528\u6237\u8d26\u6237\u53d6\u6c34')) {
    return t('transactions.displayType.userDispense', { defaultValue: 'User account dispense' });
  }
  return containsChineseText(rawDisplay) ? (key || 'Other transaction') : rawDisplay || key || '-';
};

export const getStatusLabel = (t, namespace, status, fallback = '-') => {
  const key = String(status || '').trim();
  if (!key) return fallback;
  return t(`${namespace}.${key}`, { defaultValue: key });
};

export const getInspectionItemText = (t, item = {}, field = 'title') => {
  const itemId = String(item.itemId || '').replace(/[.-]/g, '_');
  const fallback = item[field] || item.itemId || '-';
  if (!itemId) return fallback;
  return t(`inspectionItems.${itemId}.${field}`, { defaultValue: fallback });
};

export const getInspectionFreeText = (t, value) => {
  const text = String(value || '').trim();
  if (!text) return text;

  if (['\u65e0', 'none', 'None', '-'].includes(text)) {
    return t('common.none', { defaultValue: '-' });
  }

  if (['\u65e0\u5f02\u5e38', '\u6b63\u5e38', 'normal', 'Normal', 'OK', 'ok'].includes(text)) {
    return t('inspectionText.noIssue', { defaultValue: 'No issue' });
  }

  const noIssueMatch = text.match(/^\u5b8c\u6210\s+(\d+)\/(\d+)\s+\u9879\u5de1\u68c0\uff0c\u672a\u53d1\u73b0\u5f02\u5e38\u3002?$/u);
  if (noIssueMatch) {
    return t('inspectionText.summaryNoIssue', {
      completed: noIssueMatch[1],
      total: noIssueMatch[2],
      defaultValue: `Completed ${noIssueMatch[1]}/${noIssueMatch[2]} inspection items, no issue found.`
    });
  }

  const abnormalMatch = text.match(/^\u5b8c\u6210\s+(\d+)\/(\d+)\s+\u9879\u5de1\u68c0\uff0c\u53d1\u73b0\s+(\d+)\s+\u9879\u5f02\u5e38\u3002?$/u);
  if (abnormalMatch) {
    return t('inspectionText.summaryWithIssue', {
      completed: abnormalMatch[1],
      total: abnormalMatch[2],
      abnormal: abnormalMatch[3],
      defaultValue: `Completed ${abnormalMatch[1]}/${abnormalMatch[2]} inspection items, found ${abnormalMatch[3]} abnormal items.`
    });
  }

  return containsChineseText(value)
    ? t('inspectionText.unavailable', { defaultValue: 'Inspection details unavailable' })
    : value;
};

export const getWatermarkLabel = (t, status) => {
  const key = status || 'unknown';
  return t(`watermarkStatus.${key}`, { defaultValue: status || '-' });
};

export const getIotAlertLabel = (t, errorCode, fallback = '') => {
  if (!errorCode) return fallback || '-';
  return t(`deviceAlertsPage.iotFaults.${errorCode}`, { defaultValue: fallback || errorCode });
};

export const getAlertMessage = (t, alert = {}) => {
  const deviceName = normalizeAutogeneratedDeviceName(
    alert.unit?.deviceName || alert.unit?.location,
    alert.unit?.deviceId || alert.unitId || '-'
  );
  const errorCode = alert.errorCode || '-';
  const faultLabel = getIotAlertLabel(t, alert.errorCode, errorCode);

  if (alert.source === 'telemetry' && alert.sourceKey === 'ro_tds_over_50') {
    const value = String(alert.alertMessage || '').match(/\bTDS\s+(-?\d+(?:\.\d+)?)/i)?.[1] || '-';
    return t('deviceAlertsPage.messages.roTdsHigh', {
      value,
      threshold: 50,
      defaultValue: `RO water TDS exceeded 50 twice; latest reading: ${value}`
    });
  }

  if (alert.alertType === 'offline') {
    return t('deviceAlertsPage.messages.offline', {
      device: deviceName,
      minutes: 5,
      defaultValue: `${deviceName} has been offline for more than 5 minutes`
    });
  }

  if (alert.alertType === 'low_quality') {
    return t('deviceAlertsPage.messages.lowQuality', {
      errorCode,
      faultLabel,
      defaultValue: `Water quality warning: ${faultLabel}`
    });
  }

  if (alert.alertType === 'fault') {
    return t('deviceAlertsPage.messages.fault', {
      errorCode,
      faultLabel,
      defaultValue: `Device fault: ${faultLabel}`
    });
  }

  if (alert.alertType === 'maintenance') {
    return t('deviceAlertsPage.messages.maintenance', {
      errorCode,
      faultLabel,
      defaultValue: `Maintenance warning: ${faultLabel}`
    });
  }

  return alert.alertMessage || t('deviceAlertsPage.noMessage');
};
